import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';

import RouteGetter from '../../../../routes/RouteGetter';
import { ButtonGroup, SimpleButton } from '../../../../components';
import { Project } from '../../../../types/items';
import { ButtonOption } from '../../../../components/button/ButtonGroup';
import { saveProjectRequest } from '../../../../store/modules/projects/actions';
import useStyles from '../styles';
import { selectAllowChangeProjectStatus } from '../../../../store/modules/projects/selectors';
import ProjectStatusChangeConfirmModal from './ProjectStatusChangeConfirmModal';
import VideoEditStepModal from '../../modals/VideoEditStepModal/VideoEditStepModal';

type Props = {
  project: Project;
};

const ProjectStatusGroupActions = ({ project }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { params: { id: projectId } } = useRouteMatch<{ id: string }>();
  const canEdit = project?.permissions.can_edit;
  const [show, setShow] = useState(true);
  const allowChangeProjectStatus = useSelector(selectAllowChangeProjectStatus);
  const [showModal, setShowModal] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const buttonGroupStyles = {
    grouped: classes.mainButton,
    contained: classes.buttonGroupContained,
  };
  const [projectStatusId, setProjectStatusId] = useState(0);

  const updateProjectHandler = useCallback((statusId: number = projectStatusId) => {
    const newProject = { ...project } as Project;
    newProject.status_id = statusId;
    dispatch(saveProjectRequest(newProject));
    dispatch(push(`/dashboard/projects/${project.id}/activity`));
    setShow(false);
  }, [dispatch, project, setShow, projectStatusId]);

  const updateStatusHandler = useCallback((statusId: number, checkPermission: boolean = true) => {
    setProjectStatusId(statusId);
    if (checkPermission && !allowChangeProjectStatus) {
      setShowModal(true);
      return;
    }

    updateProjectHandler(statusId);
    dispatch(push(RouteGetter.get('project').activity(projectId)));
  }, [updateProjectHandler, dispatch, projectId, allowChangeProjectStatus]);

  const approveChangeStatus = useCallback(() => {
    setShowModal(false);
    updateProjectHandler(projectStatusId);
    dispatch(push(RouteGetter.get('project').activity(projectId)));
  }, [updateProjectHandler, dispatch, projectId, projectStatusId]);

  const buttons: ButtonOption[] = useMemo(() => {
    const arr: ButtonOption[] = [];
    if (canEdit && [1, 2, 7, 8, 9].includes(project.status_id)) { // statuses: planning & recording
      arr.push({
        title: 'Request Video Edit',
        onClick: () => setShowStepModal(true),
        disabled: false,
        path: [],
      });
    }

    if (canEdit && project.status_id === 5) { // status: ready for customer review
      arr.push({
        title: 'Approved',
        onClick: () => updateStatusHandler(7),
        disabled: false,
        path: [],
      });
      arr.push({
        title: 'Needs more work',
        onClick: () => updateStatusHandler(6),
        disabled: false,
        path: [],
      });
    }
    return arr;
  }, [project, canEdit, updateStatusHandler]);

  const selectDefault = useCallback(() => buttons
    .findIndex((item, index) => index === 0), [buttons]);

  useEffect(() => {
    setShow(true);
  }, [project, setShow]);

  if (!buttons.length) {
    return null;
  }

  if (buttons.length === 1) {
    return (
      <div>
        <SimpleButton
          onClick={buttons[0].onClick}
          additionalClassName={classes.changeStatusButton}
        >
          {buttons[0].title}
        </SimpleButton>
        <ProjectStatusChangeConfirmModal
          open={showModal}
          cancelCallback={approveChangeStatus}
          approveCallback={() => setShowModal(false)}
        />
        {showStepModal && (
          <VideoEditStepModal
            open={showStepModal}
            close={() => setShowStepModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div hidden={!show}>
      <ButtonGroup
        buttons={buttons}
        selectedDefault={selectDefault}
        disableUpdateMainButton={false}
        buttonGroupClasses={buttonGroupStyles}
      />
      {showStepModal && (
        <VideoEditStepModal
          open={showStepModal}
          close={() => setShowStepModal(false)}
        />
      )}
    </div>
  );
};

export default ProjectStatusGroupActions;
